import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/gcstudent/bookdetailpage/BookDetailPage.css";
import AppContext from "../../../context/AppContext";

const Toast = ({ message, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="gcStudent-toast gcStudent-toast-anim-in">
      <span className="gcStudent-toast__icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#16a34a" />
          <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="gcStudent-toast__msg">{message}</span>
    </div>
  );
};

/* ══════════════════════════════════════════════
   Main — Book Detail Page
   ✅ Login/role check AppContext (loggedInUser + userType) se
   ✅ Reserve click:
      - not logged in  -> /select-login
      - logged in (student/teacher) -> seedha /confirm-reservation
   ✅ available_copies (backend-computed: total_copies − active
      reserved/issued bookings) drives the Reserve button — total_copies
      is shown separately as the library's total stock.
══════════════════════════════════════════════ */
const BookDetailPage = ({ book, onBack }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const { loggedInUser, userType, setSelectedBookForReservation } =
    useContext(AppContext);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const accession_no = book?.accession_no || "N/A";
  const totalCopies = book?.total_copies ?? 0;
  // Fall back to totalCopies only if the backend hasn't sent available_copies yet
  const availableCopies = book?.available_copies ?? totalCopies;

  const imageUrl = book?.img
    ? book.img.startsWith("http")
      ? book.img
      : `http://127.0.0.1:8000/storage/${book.img}`
    : "https://via.placeholder.com/300x420?text=No+Image";

  const handleReserveClick = () => {
    if (availableCopies === 0) return; // extra guard, button is disabled anyway

    localStorage.setItem("pendingReservation", JSON.stringify(book));
    setSelectedBookForReservation(book);

    if (!loggedInUser?.id) {
      navigate("/select-login");
      return;
    }

    if (userType === "student" || userType === "teacher") {
      navigate("/confirm-reservation");
    } else {
      navigate("/select-login");
    }
  };

  return (
    <div className={`gcStudent-detail-page ${visible ? "gcStudent-detail-visible" : ""}`}>
      <Container fluid="lg">
        <button className="gcStudent-back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Library
        </button>

        <Row className="gcStudent-detail-row gy-4">
          <Col xs={12} md={5} lg={4}>
            <div className="gcStudent-detail-img-wrap">
              <img src={imageUrl} alt={book?.title || "Book"} className="gcStudent-detail-img" />
            </div>
          </Col>

          <Col xs={12} md={7} lg={8}>
            <div className="gcStudent-detail-info">
              <span className="gcStudent-detail-badge">{book?.dept || "General"}</span>
              <h1 className="gcStudent-detail-title">{book?.title || "Untitled"}</h1>

              <div className="gcStudent-detail-meta">
                <span className="gcStudent-meta-pill">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  <span className="gcStudent-meta-label">Author:</span>
                  <span className="gcStudent-meta-value">{book?.author || "Unknown"}</span>
                </span>

                <span className="gcStudent-meta-pill">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M4 6h16M4 10h16M4 14h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <span className="gcStudent-meta-label">Accession No:</span>
                  <span className="gcStudent-meta-value">{accession_no}</span>
                </span>

                <span className="gcStudent-meta-pill">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M9 7h6M9 11h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <span className="gcStudent-meta-label">Copies:</span>
                  <span className="gcStudent-meta-value">{totalCopies} Total</span>
                </span>
              </div>

              <div className="gcStudent-detail-avail-row">
                <span className={`gcStudent-meta-pill gcStudent-avail-pill ${availableCopies === 0 ? "gcStudent-avail-none" : "gcStudent-avail-yes"}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M7 7h10M7 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  <span className="gcStudent-meta-label">Available:</span>
                  <span className="gcStudent-meta-value">{availableCopies} Available</span>
                </span>
              </div>

              <p className="gcStudent-detail-desc">
                A comprehensive guide to {(book?.title || "").toLowerCase()} used in{" "}
                {(book?.dept || "general").toLowerCase()} studies.
              </p>

              <div className="gcStudent-detail-actions">
                <button
                  className="gcStudent-action-primary"
                  disabled={availableCopies === 0}
                  onClick={handleReserveClick}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  {availableCopies === 0 ? "Not Available" : "Reserve Book"}
                </button>
              </div>

              {book?.aiSummary && (
                <div className="gcStudent-ai-card">
                  <div className="gcStudent-ai-header">
                    <span className="gcStudent-ai-icon">🤖</span>
                    <span className="gcStudent-ai-label">AI Summary</span>
                  </div>
                  <p className="gcStudent-ai-text">{book.aiSummary}</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BookDetailPage;